import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Checkbox from '../../Fundamentals/Form/Checkbox';
import type { VossFeatureFlag } from './VossFeatureFlags.types';

interface VossFeatureFlagsTableProps {
    flags: VossFeatureFlag[];
    // names of flags with a request in flight; their controls are disabled
    pendingNames: ReadonlySet<string>;
    onToggle: (name: string, enabled: boolean) => void;
    onSaveDescription: (name: string, description: string | null) => void;
    onDelete: (name: string) => void;
}

export const formatFlagEvent = (time: string | null | undefined, userName: string | null | undefined, userReference: string | null | undefined): string => {
    if (time === null || time === undefined) return '';
    const who = userName ?? userReference;
    const formatted = new Date(time).toLocaleString('sv-SE');
    return who === null || who === undefined ? formatted : `${formatted} (${who})`;
};

const VossFeatureFlagsTable = ({ flags, pendingNames, onToggle, onSaveDescription, onDelete }: VossFeatureFlagsTableProps) => {
    const { t } = useTranslation();
    // An open description edit lives here, so toggling the flag never touches (or sends) the unsaved text.
    const [editing, setEditing] = useState<{ name: string; description: string } | null>(null);

    const saveDescription = (flag: VossFeatureFlag) => {
        if (editing === null) return;
        const description = editing.description.trim();
        onSaveDescription(flag.name, description === '' ? null : description);
        setEditing(null);
    };

    return (
        <table className="table table-stripe table-hover">
            <thead>
                <tr>
                    <th>{t('vossfeatureflags.name')}</th>
                    <th>{t('vossfeatureflags.enabled')}</th>
                    <th>{t('vossfeatureflags.description')}</th>
                    <th>{t('vossfeatureflags.ticket')}</th>
                    <th>{t('vossfeatureflags.owner')}</th>
                    <th>{t('vossfeatureflags.lasttoggled')}</th>
                    <th>{t('vossfeatureflags.lastmodified')}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {flags.map((flag) => {
                    const pending = pendingNames.has(flag.name);
                    return (
                        <tr key={flag.name}>
                            <td>{flag.name}</td>
                            <td>
                                <Checkbox
                                    id={`voss-flag-${flag.name}`}
                                    label={<span className="sr-only">{t('vossfeatureflags.enabledLabel', { name: flag.name })}</span>}
                                    checked={flag.enabled}
                                    disabled={pending}
                                    onChange={(enabled) => onToggle(flag.name, enabled)}
                                />
                            </td>
                            <td>
                                {editing?.name === flag.name ? (
                                    <div className="d-flex">
                                        <input
                                            className="form-control"
                                            aria-label={String(t('vossfeatureflags.description'))}
                                            value={editing.description}
                                            disabled={pending}
                                            onChange={(e) => setEditing({ name: flag.name, description: e.target.value })}
                                        />
                                        <button type="button" className="btn btn-primary btn-sm" disabled={pending} onClick={() => saveDescription(flag)}>
                                            {t('common.save')}
                                        </button>
                                        <button type="button" className="btn btn-sm" disabled={pending} onClick={() => setEditing(null)}>
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                ) : (
                                    <span>
                                        {flag.description ?? ''}{' '}
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            disabled={pending}
                                            onClick={() => setEditing({ name: flag.name, description: flag.description ?? '' })}
                                        >
                                            {t('common.edit')}
                                        </button>
                                    </span>
                                )}
                            </td>
                            <td>{flag.ticket}</td>
                            <td>{flag.owner}</td>
                            <td>{formatFlagEvent(flag.toggled, flag.toggledByName, flag.toggledBy)}</td>
                            <td>{formatFlagEvent(flag.modified, flag.modifiedByName, flag.modifiedBy)}</td>
                            <td>
                                <button type="button" className="btn btn-destructive btn-sm" disabled={pending} onClick={() => onDelete(flag.name)}>
                                    {t('common.delete')}
                                </button>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export default VossFeatureFlagsTable;
